import { useEffect, useState } from 'react';
import {
    componentSchemeToSwatch,
    COMPONENT_COLOR_SCHEME_GROUPS,
} from '../helpers/colorScheme';
import { useEditor } from '../context/EditorContext/EditorContext.jsx';
import ColorSchemeSelect from '../ui/ColorSchemeSelect';
import FontAwesomeIconPicker from '../ui/FontAwesomeIconPicker';
import { SuggestInput } from './SuggestInput.jsx';

const DEFAULT_TEXT =
    'We use cookies to improve your experience, analyze site traffic, and personalize content. You can accept all cookies or decline non-essential ones.';
const DEFAULT_COLOR_SCHEME = 'light';
const DEFAULT_ICON = 'fa-solid fa-cookie-bite';

// Follows Nav's own color scheme picker: box/effect treatments don't apply
// to a plain banner surface, and "Transparent" doesn't make sense without a
// hero behind it. "Float" is kept — it's what turns the banner into a
// rounded, inset card instead of a flush, square-cornered bar.
const BANNER_COLOR_SCHEME_GROUPS = COMPONENT_COLOR_SCHEME_GROUPS.filter(
    (group) => !['Box Effects', 'Effects', 'Transparent'].includes(group.label)
);

async function fetchSiteRoutes() {
    try {
        const response = await fetch('/api/site-routes', {
            cache: 'no-store',
        });
        if (!response.ok) return [];
        const payload = await response.json();
        return Array.isArray(payload?.pages) ? payload.pages : [];
    } catch {
        return [];
    }
}

function normalizeRoute(value) {
    let v = String(value || '').trim();
    if (!v) return '';
    if (!v.startsWith('/')) v = `/${v}`;
    v = v.replace(/\/+$/, '') || '/';
    return v;
}

function chipClassName(active) {
    return `rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
        active
            ? 'border-primary-700 bg-primary-700 text-white'
            : 'border-neutral-300 text-neutral-600 hover:bg-neutral-100'
    }`;
}

function TextField({ label, value, placeholder, onChange }) {
    return (
        <div>
            <label className="mb-1 block text-[11px] font-medium leading-5 text-neutral-400">
                {label}
            </label>
            <div className="rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                <div className="flex h-[30px] items-stretch overflow-hidden rounded-[10px] bg-white">
                    <input
                        type="text"
                        spellCheck={false}
                        placeholder={placeholder}
                        className="flex-1 appearance-none border-0 bg-transparent px-2 text-[12px] leading-6 text-neutral-800 placeholder:text-neutral-400
                         focus:border-transparent focus:outline-none focus:ring-0 focus:ring-offset-0"
                        value={value}
                        onChange={onChange}
                    />
                </div>
            </div>
        </div>
    );
}

export function CookieConsent() {
    const editor = useEditor();
    const data = editor.global.data || {};

    const enabled = Boolean(data.cookie_consent_enabled);
    const mode =
        data.cookie_consent_mode === 'granular' ? 'granular' : 'simple';
    const policyLink =
        normalizeRoute(data.cookie_consent_policy_link) || '/privacy';
    const colorScheme =
        data.cookie_consent_color_scheme || DEFAULT_COLOR_SCHEME;
    const icon = data.cookie_consent_icon || DEFAULT_ICON;
    const showReopenIcon = Boolean(data.cookie_consent_show_reopen_icon);
    const reopenIconPosition =
        data.cookie_consent_reopen_icon_position === 'left' ? 'left' : 'right';

    const [pages, setPages] = useState(['/', '/privacy']);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const routes = await fetchSiteRoutes();
            if (cancelled) return;
            const set = new Set(['/', '/privacy']);
            routes.forEach((route) => {
                const normalized = normalizeRoute(route);
                if (normalized) set.add(normalized);
            });
            setPages(Array.from(set));
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const update = (key, value) => editor.global.change({ key, value });

    // Once a preview is open, keep it in sync with every field below —
    // switching the color scheme (or anything else) updates the open
    // banner immediately instead of waiting for another Preview click.
    const [previewing, setPreviewing] = useState(false);
    const handlePreview = () => setPreviewing(true);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const handleClosed = () => setPreviewing(false);
        window.addEventListener(
            'editor:preview-cookie-consent-closed',
            handleClosed
        );
        return () =>
            window.removeEventListener(
                'editor:preview-cookie-consent-closed',
                handleClosed
            );
    }, []);

    useEffect(() => {
        if (!previewing || typeof window === 'undefined') return;
        window.dispatchEvent(
            new CustomEvent('editor:preview-cookie-consent', {
                detail: {
                    mode,
                    text: data.cookie_consent_text || DEFAULT_TEXT,
                    acceptButtonText:
                        data.cookie_consent_accept_text || 'Accept All',
                    declineButtonText:
                        data.cookie_consent_decline_text || 'Decline',
                    manageButtonText:
                        data.cookie_consent_manage_text || 'Manage Preferences',
                    policyLink,
                    colorScheme,
                    icon,
                    showReopenIcon,
                    reopenIconPosition,
                },
            })
        );
    }, [
        previewing,
        mode,
        data.cookie_consent_text,
        data.cookie_consent_accept_text,
        data.cookie_consent_decline_text,
        data.cookie_consent_manage_text,
        policyLink,
        colorScheme,
        icon,
        showReopenIcon,
        reopenIconPosition,
    ]);

    return (
        <div className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <label className="mb-1 block text-[11px] font-medium leading-5 text-neutral-400">
                        Show Cookie Consent Banner
                    </label>
                    <p className="text-[11px] leading-4 text-neutral-500">
                        Ask visitors to accept or decline cookies before
                        analytics/ads scripts load on every page.
                    </p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                    <span
                        className={`text-[10px] font-semibold uppercase tracking-wide ${
                            enabled ? 'text-primary-700' : 'text-neutral-400'
                        }`}
                    >
                        {enabled ? 'Enabled' : 'Disabled'}
                    </span>
                    <button
                        type="button"
                        role="switch"
                        aria-checked={enabled}
                        onClick={() =>
                            update('cookie_consent_enabled', !enabled)
                        }
                        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors duration-200 ${
                            enabled ? 'bg-primary-700' : 'bg-neutral-300'
                        }`}
                    >
                        <span
                            className={`inline-block h-5 w-5 translate-y-0.5 rounded-full bg-white transition-transform duration-200 ${
                                enabled
                                    ? 'translate-x-[1.3rem]'
                                    : 'translate-x-0.5'
                            }`}
                        />
                    </button>
                </div>
            </div>

            <div className="mt-3 flex justify-end">
                <button
                    type="button"
                    onClick={handlePreview}
                    className="inline-flex h-5 items-center rounded-full bg-secondary-700 px-2.5 text-[11px] font-semibold leading-none text-white shadow-sm transition-colors hover:bg-secondary-600 focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:ring-offset-1"
                >
                    Click to Preview
                </button>
            </div>

            {enabled && (
                <div className="mt-4 flex flex-col gap-4 border-t border-neutral-100 pt-4">
                    <div>
                        <label className="mb-1 block text-[11px] font-medium leading-5 text-neutral-400">
                            Consent Level
                        </label>
                        <div className="flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={() =>
                                    update('cookie_consent_mode', 'simple')
                                }
                                className={chipClassName(mode === 'simple')}
                            >
                                Simple (Accept / Decline)
                            </button>
                            <button
                                type="button"
                                onClick={() =>
                                    update('cookie_consent_mode', 'granular')
                                }
                                className={chipClassName(mode === 'granular')}
                            >
                                Granular (by category)
                            </button>
                        </div>
                        <p className="mt-2 text-[11px] leading-4 text-neutral-500">
                            Simple shows one Accept/Decline choice. Granular
                            adds a &quot;Manage Preferences&quot; panel with
                            Necessary/Analytics/Marketing toggles — use this for
                            sites that need GDPR-style opt-in.
                        </p>
                    </div>

                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <label className="mb-1 block text-[11px] font-medium leading-5 text-neutral-400">
                                Let Visitors Reopen Their Choice
                            </label>
                            <p className="text-[11px] leading-4 text-neutral-500">
                                Shows a small icon after a decision so visitors
                                can reopen it later.
                            </p>
                        </div>
                        <button
                            type="button"
                            role="switch"
                            aria-checked={showReopenIcon}
                            onClick={() =>
                                update(
                                    'cookie_consent_show_reopen_icon',
                                    !showReopenIcon
                                )
                            }
                            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors duration-200 ${
                                showReopenIcon
                                    ? 'bg-primary-700'
                                    : 'bg-neutral-300'
                            }`}
                        >
                            <span
                                className={`inline-block h-5 w-5 translate-y-0.5 rounded-full bg-white transition-transform duration-200 ${
                                    showReopenIcon
                                        ? 'translate-x-[1.3rem]'
                                        : 'translate-x-0.5'
                                }`}
                            />
                        </button>
                    </div>

                    {showReopenIcon && (
                        <div>
                            <label className="mb-1 block text-[11px] font-medium leading-5 text-neutral-400">
                                Icon Position
                            </label>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    onClick={() =>
                                        update(
                                            'cookie_consent_reopen_icon_position',
                                            'left'
                                        )
                                    }
                                    className={chipClassName(
                                        reopenIconPosition === 'left'
                                    )}
                                >
                                    Bottom Left
                                </button>
                                <button
                                    type="button"
                                    onClick={() =>
                                        update(
                                            'cookie_consent_reopen_icon_position',
                                            'right'
                                        )
                                    }
                                    className={chipClassName(
                                        reopenIconPosition === 'right'
                                    )}
                                >
                                    Bottom Right
                                </button>
                            </div>
                        </div>
                    )}

                    <div>
                        <label className="mb-1 block text-[11px] font-medium leading-5 text-neutral-400">
                            Icon
                        </label>
                        <FontAwesomeIconPicker
                            value={icon}
                            onChange={(next) =>
                                update('cookie_consent_icon', next)
                            }
                        />
                    </div>

                    <div>
                        <label className="mb-1 block text-[11px] font-medium leading-5 text-neutral-400">
                            Color Scheme
                        </label>
                        <ColorSchemeSelect
                            value={colorScheme}
                            onChange={(next) =>
                                update('cookie_consent_color_scheme', next)
                            }
                            groups={BANNER_COLOR_SCHEME_GROUPS}
                            swatchFn={componentSchemeToSwatch}
                        />
                    </div>

                    <div>
                        <label className="mb-1 block text-[11px] font-medium leading-5 text-neutral-400">
                            Banner Text
                        </label>
                        <div className="rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                            <div className="flex min-h-[64px] items-stretch overflow-hidden rounded-[10px] bg-white">
                                <textarea
                                    rows={3}
                                    className="flex-1 resize-y appearance-none border-0 bg-transparent px-2 py-1.5 text-[12px] leading-5 text-neutral-800 placeholder:text-neutral-400
                             focus:border-transparent focus:outline-none focus:ring-0 focus:ring-offset-0"
                                    placeholder={DEFAULT_TEXT}
                                    value={data.cookie_consent_text || ''}
                                    onChange={(e) =>
                                        update(
                                            'cookie_consent_text',
                                            e.target.value
                                        )
                                    }
                                />
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <TextField
                            label="Accept Button Text"
                            placeholder="Accept All"
                            value={data.cookie_consent_accept_text || ''}
                            onChange={(e) =>
                                update(
                                    'cookie_consent_accept_text',
                                    e.target.value
                                )
                            }
                        />
                        <TextField
                            label="Decline Button Text"
                            placeholder="Decline"
                            value={data.cookie_consent_decline_text || ''}
                            onChange={(e) =>
                                update(
                                    'cookie_consent_decline_text',
                                    e.target.value
                                )
                            }
                        />
                    </div>

                    {mode === 'granular' && (
                        <TextField
                            label="Manage Preferences Button Text"
                            placeholder="Manage Preferences"
                            value={data.cookie_consent_manage_text || ''}
                            onChange={(e) =>
                                update(
                                    'cookie_consent_manage_text',
                                    e.target.value
                                )
                            }
                        />
                    )}

                    <div>
                        <label className="mb-1 block text-[11px] font-medium leading-5 text-neutral-400">
                            Policy Link (Learn More)
                        </label>
                        <div className="flex">
                            <SuggestInput
                                value={policyLink}
                                onChange={(value) =>
                                    update('cookie_consent_policy_link', value)
                                }
                                options={pages}
                                placeholder="/privacy"
                            />
                        </div>
                        <p className="mt-2 text-[11px] leading-4 text-neutral-500">
                            Pick an existing page or type a custom URL. Defaults
                            to /privacy.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}
